import * as React from "react";

import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "./command";
import { cn } from "@/lib/utils";
import { Badge } from "./badge";

export type OptionType = {
  label: string;
  value: any;
  disabled?: boolean;
}

interface MultiSelectProps {
  options: OptionType[];
  selected: OptionType[];
  onChange: (selected: OptionType[]) => any;
  className?: string;
  placeholder?: string;
  customSelectedText?: string;
  disabled?: boolean;
  showSelectAllAndNone?: boolean;
}

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      selected,
      onChange,
      className,
      customSelectedText,
      disabled,
      showSelectAllAndNone,
      placeholder,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);

    const handleUnselect = (item: OptionType) => {
      onChange(selected.filter((i) => i.value !== item.value));
    };

    // on delete key press, remove last selected item
    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Backspace" && selected.length > 0) {
          onChange(
            selected.filter((_, index) => index !== selected.length - 1)
          );
        }

        // close on escape
        if (e.key === "Escape") {
          setOpen(false);
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [onChange, selected]);

    return (
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild className={`max-h-[36px] ${className}`}>
          <Button
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`w-full justify-between bg-white px-3 text-black max-h-none ${selected?.length > 1 ? "h-fit" : "h-10"
            }`}
            onClick={() => setOpen(!open)}
            disabled={disabled}
          >
            <div className="flex flex-wrap items-center gap-1">
              {customSelectedText && selected?.length > 0
                ? customSelectedText
                : selected?.map((item) => (
                  <Badge
                    variant="outline"
                    key={item?.value}
                    className="flex items-center gap-1"
                    onClick={() => handleUnselect(item)}
                  >
                    {item?.label}
                    <Button
                      variant="outline"
                      size="default"
                      className="border-none p-1 h-fit"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleUnselect(item);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUnselect(item);
                      }}
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground"/>
                    </Button>
                  </Badge>
                ))}
              {selected?.length === 0 && (
                <span>{placeholder ?? "Select ..."}</span>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50"/>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" side="bottom" align="start">
          <Command className={className}>
            <CommandInput placeholder="Search ..."/>
            <CommandEmpty>No item found.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {
                showSelectAllAndNone && (
                  <div className="flex flex-row gap-2 w-full justify-center">
                    <CommandItem
                      className="grow justify-center"
                      onSelect={() => onChange(options.filter(option => !option.disabled))}
                    >
                      All
                    </CommandItem>
                    <CommandItem
                      className="grow justify-center"
                      onSelect={() => onChange([])}
                    >
                      None
                    </CommandItem>
                  </div>
                )
              }
              {
                options
                  .sort((option1, option2) => {
                    if (option1.disabled !== option2.disabled) {
                      return option1.disabled ? 1 : -1;
                    }
                    return option1.label.localeCompare(option2.label)
                  })
                  .map((option) => (
                    <CommandItem
                      key={option?.value}
                      disabled={option.disabled}
                      onSelect={() => {
                        onChange(
                          selected?.some((item) => item?.value === option?.value)
                            ? selected.filter(
                              (item) => item?.value !== option?.value
                            )
                            : [...selected, option]
                        );
                        setOpen(true);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selected?.some((item) => item?.value === option?.value)
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };
